import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiChevronDown, FiGrid, FiLock, FiX } from 'react-icons/fi';
import GlassCard from '../../components/ui/GlassCard';
import CarrierCredentialModal from './CarrierCredentialModal';
import CarrierActionRunner from './CarrierActionRunner';
import CarrierSessionBar from './CarrierSessionBar';
import CarrierAnswerRules from './CarrierAnswerRules';
import CarrierLoginStore from './CarrierLoginStore';
import LogInAllCarriers, { type LoginProgress } from './LogInAllCarriers';
import CarrierHandoffFrame from '../chat/CarrierHandoffFrame';
import { CARRIER_CATALOG, LINES_OF_BUSINESS, carrierCapabilitySummary, carrierLines, visibleActions, type CarrierDefinition, type LineOfBusiness } from './carrierAccessCatalog';
import {
  getConnectionStates,
  saveCarrierCredentials,
  setActionEnabled,
  disconnectCarrier,
  getHeadedPreferences,
  setHeadedPreference,
  type CarrierConnectionState,
} from '../../services/carrierAccessService';
import {
  cancelCarrierRuns,
  cancelRun,
  getActiveRuns,
  getCredentialsStatus,
  getAgencyCredentialsStatus,
  getCachedPolicies,
  setAttachedAgent,
  getRunLogs,
  getCarrierSettings,
  setPlaceholdersEnabled,
  getNotifyEmail,
  saveNotifyEmail,
  signedInEmail,
  getCarrierLocks,
  unlockCarrier,
  relockCarrier,
  type CarrierLocks,
  type CarrierPolicy,
  type CredentialStatus,
  type RunLogEntry,
} from '../../services/automationService';
import '../../styles/CarrierAccess.css';

// The same agency id the rest of the app already saves on sign-in (see
// authService.login) — used to scope saved carrier logins to this agency,
// so two agencies signed into Momentum AMP never see each other's carrier
// connections. Falls back to a stable local id when unset (dev/no login).
function currentAgencyId(): string {
  return localStorage.getItem('NCAgencyId') || 'local-dev';
}

const CarrierAccessPage: React.FC = () => {
  const agencyId = useMemo(() => currentAgencyId(), []);
  const [connections, setConnections] = useState<Record<string, CarrierConnectionState>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Locked carriers (National General, Travelers): a padlock until the unlock code is typed here; the server re-locks after a while.
  const [locks, setLocks] = useState<CarrierLocks | null>(null);
  const [unlockFor, setUnlockFor] = useState<string | null>(null);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const refreshLocks = () => { getCarrierLocks().then(setLocks).catch(() => setLocks(null)); };
  useEffect(() => {
    refreshLocks();
    const timer = window.setInterval(refreshLocks, 60_000);
    return () => window.clearInterval(timer);
  }, []);
  // Locked unless the server says it was unlocked: if it cannot be reached, stay locked.
  const isLocked = (c: CarrierDefinition) => !!c.locked && !(locks && !locks.locked.includes(c.id));
  const handleUnlock = async (carrier: CarrierDefinition) => {
    try {
      await unlockCarrier(carrier.id, unlockCode);
      setUnlockCode(''); setUnlockError(null); setUnlockFor(null); refreshLocks();
    } catch (err) {
      setUnlockError(err instanceof Error ? err.message : 'Could not unlock.');
    }
  };
  const [credentialCarrier, setCredentialCarrier] = useState<CarrierDefinition | null>(null);
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  // Shared per-carrier, not per-action — so a policy number fetched under
  // "View Policy List" shows up as a suggestion in "Retrieve a Document" too.
  const [policiesByCarrier, setPoliciesByCarrier] = useState<Record<string, CarrierPolicy[]>>({});
  // Clicking a policy card under "View Policy List" fills this in, and
  // "Retrieve a Document" picks it up — so finding a policy and pulling a
  // document for it is one click, not a search-and-retype.
  const [selectedPolicyByCarrier, setSelectedPolicyByCarrier] = useState<Record<string, string>>({});
  const [lineFilter, setLineFilter] = useState<LineOfBusiness | 'All'>('All');
  // Progress of "Log in to all carriers" per carrier, shown on each tile (and its hand-off card when it needs the person).
  const [loginProgress, setLoginProgress] = useState<Record<string, LoginProgress>>({});
  // Carriers with something running or waiting on a person right now (any login, check or job), so each tile can show an X.
  const [busyCarriers, setBusyCarriers] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      const runs = await getActiveRuns().catch(() => []);
      if (!alive) return;
      const next: Record<string, number> = {};
      for (const r of runs) if (r.carrier) next[r.carrier] = (next[r.carrier] ?? 0) + 1;
      setBusyCarriers((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    void poll();
    const timer = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  // Real, server-verified fact: does .env.local actually have credentials
  // for this carrier? Takes priority over the local manual-toggle fallback
  // below — "Connected" should mean something true, not just a flipped
  // switch, wherever we can back it with a real check.
  const [credentialsStatus, setCredentialsStatus] = useState<Record<string, CredentialStatus>>({});
  // Real, per-agency saved logins (see /agency-credentials) — takes
  // priority over the .env.local fallback above so each agency's own
  // connection state is what actually drives "Connected", not just yours.
  const [agencyCredentialsStatus, setAgencyCredentialsStatus] = useState<Record<string, CredentialStatus>>({});
  // Carrier-level (not per-quote) preference — when on, Create Quote runs
  // headed and leaves the browser open for you to finish or take over.
  const [headedByCarrier, setHeadedByCarrier] = useState<Record<string, boolean>>({});
  // Global "Use placeholder info" switch — server-side, applies to every
  // quote job across every carrier (see /carrier-settings in server.mjs).
  // Requested specifically as one flip-off switch for going live with real
  // customer quotes, so this is never per-carrier or per-browser-tab.
  const [placeholdersEnabled, setPlaceholdersEnabledState] = useState(false);

  // Where this person wants retrieved documents emailed, saved per signed-in
  // user on the automation server.
  const [notifyDraft, setNotifyDraft] = useState('');
  const [notifySaved, setNotifySaved] = useState<string | null>(null);
  const handleSaveNotifyEmail = async () => {
    const user = signedInEmail();
    if (!user) {
      setSaveNotice("Couldn't tell who is signed in, so the email wasn't saved.");
      return;
    }
    try {
      const saved = await saveNotifyEmail(user, notifyDraft.trim());
      setNotifySaved(saved);
      setNotifyDraft(saved);
      setSaveNotice(`Documents will be emailed to ${saved}.`);
    } catch (err) {
      setSaveNotice(err instanceof Error && err.message ? err.message : "Couldn't save that email.");
    }
  };

  const handleTogglePlaceholders = async () => {
    const next = !placeholdersEnabled;
    setPlaceholdersEnabledState(next); // optimistic — reverted below on failure
    try {
      await setPlaceholdersEnabled(next);
    } catch {
      setPlaceholdersEnabledState(!next);
      setSaveNotice("Couldn't reach the automation server to update the placeholder-info setting.");
      window.setTimeout(() => setSaveNotice(null), 5000);
    }
  };

  const refreshAgencyCredentialsStatus = () => {
    getAgencyCredentialsStatus(agencyId).then(setAgencyCredentialsStatus).catch(() => setAgencyCredentialsStatus({}));
  };

  const handleToggleHeaded = (carrier: CarrierDefinition) => {
    const next = !headedByCarrier[carrier.id];
    setHeadedPreference(carrier.id, next);
    setHeadedByCarrier((prev) => ({ ...prev, [carrier.id]: next }));
  };

  useEffect(() => {
    setConnections(getConnectionStates());
    setHeadedByCarrier(getHeadedPreferences());
    // Whatever machine is running this build, its automation/.env.local is
    // the shared local credential store for this build — every signed-in
    // agency sees the same "Connected" fact from it, since there's only one
    // machine's worth of carrier logins in play here.
    getCredentialsStatus().then(setCredentialsStatus).catch(() => setCredentialsStatus({}));
    refreshAgencyCredentialsStatus();
    getCarrierSettings().then((s) => setPlaceholdersEnabledState(s.placeholdersEnabled)).catch(() => {});
    const user = signedInEmail();
    if (user) {
      getNotifyEmail(user)
        .then((email) => {
          setNotifySaved(email);
          setNotifyDraft(email ?? user);
        })
        .catch(() => setNotifyDraft(user));
    }
    // Show whatever "View Policy List" already found last time it ran
    // (refreshed periodically by scripts/refreshPolicies.ts, not a live
    // portal login) immediately on load — no toggle, no wait. A carrier
    // with no cache yet just falls through to the runner's own on-mount
    // fetch once its panel is opened.
    getCachedPolicies()
      .then((cache) => {
        const loaded: Record<string, CarrierPolicy[]> = {};
        for (const [carrierKey, entry] of Object.entries(cache)) {
          if (entry) loaded[carrierKey] = entry.policies;
        }
        setPoliciesByCarrier((prev) => ({ ...loaded, ...prev }));
      })
      .catch(() => {});
  }, []);

  // Merges the real server-verified status on top of the local fallback —
  // a carrier with real credentials shows connected with its real username
  // regardless of whether the local toggle was ever used. This agency's own
  // saved login (agencyCredentialsStatus) wins when present; the shared
  // .env.local status is only a fallback for carriers this agency hasn't
  // connected yet (keeps your own dev/testing account working as before).
  const effectiveConnections = useMemo(() => {
    const merged: Record<string, CarrierConnectionState> = { ...connections };
    for (const [carrierId, status] of Object.entries(credentialsStatus)) {
      if (status.configured) {
        merged[carrierId] = {
          carrierId,
          connected: true,
          username: status.username,
          enabledActions: connections[carrierId]?.enabledActions ?? [],
        };
      }
    }
    for (const [carrierId, status] of Object.entries(agencyCredentialsStatus)) {
      if (status.configured) {
        merged[carrierId] = {
          carrierId,
          connected: true,
          username: status.username,
          enabledActions: connections[carrierId]?.enabledActions ?? [],
        };
      }
    }
    return merged;
  }, [connections, credentialsStatus, agencyCredentialsStatus]);

  const unhiddenCarriers = useMemo(() => CARRIER_CATALOG.filter((c) => !c.hidden), []);

  // Only carriers actually on the page count — a hidden one (Mockingbird, Farmers Mutual) may still be connected underneath.
  const connectedCount = useMemo(
    () => unhiddenCarriers.filter((c) => effectiveConnections[c.id]?.connected).length,
    [effectiveConnections, unhiddenCarriers],
  );

  const visibleCarriers = useMemo(
    () => (lineFilter === 'All' ? unhiddenCarriers : unhiddenCarriers.filter((c) => carrierLines(c).includes(lineFilter))),
    [lineFilter, unhiddenCarriers],
  );

  // The expanded carrier's tile moves to the front of the grid so its
  // detail panel always lands at the top instead of breaking the 3-column
  // flow wherever that carrier happens to sit in the catalog. Collapsing it
  // sends it to the back instead of snapping back to its catalog position —
  // whatever you just finished with gets out of the way, most-recently-
  // collapsed landing furthest down.
  const [collapsedOrder, setCollapsedOrder] = useState<string[]>([]);

  const orderedCarriers = useMemo(() => {
    const expanded = expandedId ? visibleCarriers.find((c) => c.id === expandedId) : null;
    const rest = visibleCarriers.filter((c) => c.id !== expandedId);
    const normal = rest.filter((c) => !collapsedOrder.includes(c.id));
    const collapsed = collapsedOrder
      .map((id) => rest.find((c) => c.id === id))
      .filter((c): c is CarrierDefinition => !!c);
    return expanded ? [expanded, ...normal, ...collapsed] : [...normal, ...collapsed];
  }, [visibleCarriers, expandedId, collapsedOrder]);

  const handleToggleCarrier = (carrier: CarrierDefinition) => {
    if (isLocked(carrier)) {
      setUnlockFor((current) => (current === carrier.id ? null : carrier.id)); setUnlockCode(''); setUnlockError(null);
      return;
    }
    if (expandedId === carrier.id) {
      setExpandedId(null);
      setCollapsedOrder((order) => [...order.filter((id) => id !== carrier.id), carrier.id]);
    } else {
      setExpandedId(carrier.id);
      setCollapsedOrder((order) => order.filter((id) => id !== carrier.id));
    }
  };

  const handleActionToggle = (carrier: CarrierDefinition, actionKey: string, nextEnabled: boolean) => {
    const state = effectiveConnections[carrier.id];
    if (!state?.connected) {
      setPendingActionKey(nextEnabled ? actionKey : null);
      setCredentialCarrier(carrier);
      return;
    }
    setActionEnabled(carrier.id, actionKey, nextEnabled);
    setConnections(getConnectionStates());
  };

  const handleCredentialSubmit = async (username: string, password: string) => {
    if (!credentialCarrier) return;
    const { persisted } = await saveCarrierCredentials(credentialCarrier.id, { username, password }, agencyId);
    if (pendingActionKey) {
      setActionEnabled(credentialCarrier.id, pendingActionKey, true);
    }
    setConnections(getConnectionStates());
    if (persisted) refreshAgencyCredentialsStatus();
    setSaveNotice(
      persisted
        ? `${credentialCarrier.name} connected.`
        : `${credentialCarrier.name} connected locally — the automation server couldn't be reached to save the login for real runs.`,
    );
    setCredentialCarrier(null);
    setPendingActionKey(null);
    window.setTimeout(() => setSaveNotice(null), 5000);
  };

  const handleDisconnect = (carrier: CarrierDefinition) => {
    disconnectCarrier(carrier.id, agencyId);
    setConnections(getConnectionStates());
    setAgencyCredentialsStatus((prev) => {
      const next = { ...prev };
      delete next[carrier.id];
      return next;
    });
  };

  // Self-reported locally (see setAttachedAgent) — which agent this agency
  // considers this carrier connection wired up for, distinct from whether
  // the carrier login itself is valid. Only meaningful for agency-saved
  // connections, not the .env.local dev fallback.
  const [agentEditId, setAgentEditId] = useState<string | null>(null);
  const [agentDraft, setAgentDraft] = useState('');

  const handleSaveAgent = async (carrier: CarrierDefinition) => {
    await setAttachedAgent(agencyId, carrier.id, agentDraft.trim());
    refreshAgencyCredentialsStatus();
    setAgentEditId(null);
  };

  // Per-carrier run history (see appendRunLog in server.mjs) — fetched
  // lazily the first time someone opens it for a given carrier, not on
  // every panel expand, since it's a drill-down detail rather than
  // at-a-glance info.
  const [runLogsByCarrier, setRunLogsByCarrier] = useState<Record<string, RunLogEntry[]>>({});
  const [logsExpandedFor, setLogsExpandedFor] = useState<string | null>(null);

  const handleToggleLogs = async (carrier: CarrierDefinition) => {
    if (logsExpandedFor === carrier.id) {
      setLogsExpandedFor(null);
      return;
    }
    setLogsExpandedFor(carrier.id);
    if (!runLogsByCarrier[carrier.id]) {
      try {
        const logs = await getRunLogs(carrier.id);
        setRunLogsByCarrier((prev) => ({ ...prev, [carrier.id]: logs }));
      } catch {
        setRunLogsByCarrier((prev) => ({ ...prev, [carrier.id]: [] }));
      }
    }
  };

  return (
    <div className="ca-page">
      <motion.div
        className="ca-content"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="ca-header">
          <div className="ca-header__title-row">
            <div className="ca-header__icon">
              <FiGrid size={20} />
            </div>
            <div>
              <div className="ca-header__title">Carrier Access</div>
              <div className="ca-header__subtitle">
                Connect each carrier portal with your own login, then turn on the automations you want to use.
              </div>
            </div>
          </div>
          <div className="ca-header__controls">
            <LogInAllCarriers onProgress={setLoginProgress} onFinished={() => getCredentialsStatus().then(setCredentialsStatus).catch(() => {})} />
          </div>
        </div>

        <div className="ca-toolbar">
          <div className="ca-toolbar__group">
            <select
              className="ca-toolbar__select"
              value={lineFilter}
              onChange={(e) => setLineFilter(e.target.value as LineOfBusiness | 'All')}
              aria-label="Filter carriers by line of business"
            >
              <option value="All">All lines</option>
              {LINES_OF_BUSINESS.map((line) => (
                <option key={line} value={line}>{line}</option>
              ))}
            </select>
            <span className="ca-toolbar__stat">
              {connectedCount} of {unhiddenCarriers.length} carriers connected
            </span>
          </div>

          <div className="ca-toolbar__group">
            <span className="ca-toolbar__label">Use placeholder info for testing quotes</span>
            <button
              type="button"
              className={`ca-switch ${placeholdersEnabled ? 'ca-switch--on' : ''}`}
              role="switch"
              aria-checked={placeholdersEnabled}
              onClick={handleTogglePlaceholders}
            >
              <span className="ca-switch__knob" />
            </button>
            {placeholdersEnabled && <span className="ca-toolbar__warn">On, not for real customers</span>}
          </div>

          <div className="ca-toolbar__group ca-toolbar__group--email">
            <label className="ca-toolbar__label" htmlFor="ca-notify-email">Email documents to</label>
            <input
              id="ca-notify-email"
              type="email"
              className="ca-toolbar__input"
              value={notifyDraft}
              placeholder="you@momentumamp.com"
              onChange={(e) => setNotifyDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveNotifyEmail()}
            />
            <button
              type="button"
              className="ca-toolbar__save"
              disabled={!notifyDraft.trim() || notifyDraft.trim() === notifySaved}
              onClick={handleSaveNotifyEmail}
            >
              {notifySaved && notifyDraft.trim() === notifySaved ? 'Saved' : 'Save'}
            </button>
          </div>
        </div>

        {saveNotice && (
          <div className="ca-notice">
            <FiCheckCircle size={14} /> {saveNotice}
          </div>
        )}

        <div className="ca-grid">
          {visibleCarriers.length === 0 && (
            <div className="ca-empty">No carriers write {lineFilter} business yet.</div>
          )}
          {orderedCarriers.map((carrier) => {
            const state = effectiveConnections[carrier.id];
            const isAgencyVerified = !!agencyCredentialsStatus[carrier.id]?.configured;
            const isEnvVerified = !isAgencyVerified && !!credentialsStatus[carrier.id]?.configured;
            const isVerified = isAgencyVerified || isEnvVerified;
            const locked = isLocked(carrier);
            const isExpanded = expandedId === carrier.id && !locked;
            return (
              <React.Fragment key={carrier.id}>
                <GlassCard className={`ca-tile ${isExpanded ? 'ca-tile--active' : ''} ${locked ? 'ca-tile--locked' : ''}`} padding="var(--space-5)">
                  <button type="button" className="ca-tile__button" title={locked ? 'Locked: click to enter the unlock code' : undefined} onClick={() => handleToggleCarrier(carrier)}>
                    <span className="ca-tile__top">
                      <span className="ca-tile__header">
                        <span
                          className={`ca-tile__logo ${carrier.logoUrl ? 'ca-tile__logo--image' : ''}`}
                          style={{ background: carrier.logoUrl ? (carrier.logoBg ?? '#fff') : carrier.color }}
                        >
                          {carrier.logoUrl ? <img src={carrier.logoUrl} alt={carrier.name} /> : carrier.name.charAt(0)}
                        </span>
                        {locked
                          ? <FiLock size={16} className="ca-tile__lock" aria-label="Locked" />
                          : <FiChevronDown size={14} className={`ca-tile__chevron ${isExpanded ? 'ca-tile__chevron--open' : ''}`} />}
                      </span>
                      <span className="ca-tile__info">
                        <span className="ca-tile__name">{carrier.name}</span>
                        <span className="ca-tile__line-tag">{carrierLines(carrier).join(' · ')}</span>
                        {carrier.requiresVerificationCode && (
                          <span
                            className="ca-tile__2fa-tag"
                            title="This carrier's portal may ask for a verification code during a run. Keep the email or phone that receives codes handy."
                          >
                            May need 2FA code
                          </span>
                        )}
                      </span>
                      <span className="ca-tile__capabilities">
                        {carrierCapabilitySummary(carrier).map((line) => (
                          <span key={line} className="ca-tile__capability-line">{line}</span>
                        ))}
                      </span>
                    </span>
                    <span className={`ca-tile__badge ${state?.connected ? 'ca-tile__badge--connected' : ''}`}>
                      {state?.connected ? (
                        <>
                          <FiCheckCircle size={13} /> Connected
                        </>
                      ) : (
                        'Not connected'
                      )}
                      {loginProgress[carrier.id] && (
                        <span className={`ca-tile__login ca-tile__login--${loginProgress[carrier.id].state}`} title={loginProgress[carrier.id].note}>
                          {{ waiting: 'Login queued', working: 'Logging in…', 'needs-you': 'Needs you', in: 'Logged in', out: 'Not logged in', skipped: 'Skipped' }[loginProgress[carrier.id].state]}
                          {['working', 'needs-you'].includes(loginProgress[carrier.id].state) && loginProgress[carrier.id].runId && (
                            <span
                              role="button"
                              tabIndex={0}
                              title="Stop this login and skip this carrier"
                              aria-label={`Stop the ${carrier.name} login`}
                              style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); void cancelRun(loginProgress[carrier.id].runId!); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); void cancelRun(loginProgress[carrier.id].runId!); } }}
                            >
                              <FiX size={12} />
                            </span>
                          )}
                        </span>
                      )}
                      {/* Something is running for this carrier outside "Log in to all": still give the person an X to stop it. */}
                      {!loginProgress[carrier.id] && busyCarriers[carrier.id] && (
                        <span className="ca-tile__login ca-tile__login--needs-you" title="Working in this carrier's portal right now">
                          Working…
                          <span
                            role="button"
                            tabIndex={0}
                            title="Stop what is running for this carrier"
                            aria-label={`Stop what is running for ${carrier.name}`}
                            style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); void cancelCarrierRuns(carrier.id); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); void cancelCarrierRuns(carrier.id); } }}
                          >
                            <FiX size={12} />
                          </span>
                        </span>
                      )}
                      {/* No login in progress here: show what the server remembers — logged in today, until a log out or the session ends. */}
                      {!loginProgress[carrier.id] && (credentialsStatus[carrier.id]?.loggedInAt || credentialsStatus[carrier.id]?.loggedIn) && (
                        <span
                          className="ca-tile__login ca-tile__login--in"
                          title={credentialsStatus[carrier.id].loggedInAt
                            ? `Logged in at ${new Date(credentialsStatus[carrier.id].loggedInAt!).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Stays logged in for the day, until you log out or the carrier ends the session.`
                            : 'Logged in: its tab in the carrier browser is past the login page. Closing that tab logs it out.'}
                        >
                          {credentialsStatus[carrier.id].loggedInAt
                            ? `Logged in · ${new Date(credentialsStatus[carrier.id].loggedInAt!).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                            : 'Logged in'}
                        </span>
                      )}
                    </span>
                  </button>
                  {locked && unlockFor === carrier.id && (
                    <form className="ca-tile__unlock" onSubmit={(e) => { e.preventDefault(); void handleUnlock(carrier); }}>
                      <input type="password" autoFocus autoComplete="off" placeholder="Unlock code" value={unlockCode} onChange={(e) => setUnlockCode(e.target.value)} />
                      <button type="submit" disabled={!unlockCode}>Unlock</button>
                      {unlockError && <span className="ca-tile__unlock-error">{unlockError}</span>}
                    </form>
                  )}
                  {carrier.locked && !locked && (
                    <button type="button" className="ca-tile__relock" onClick={() => { void relockCarrier(carrier.id).then(refreshLocks); if (expandedId === carrier.id) setExpandedId(null); }}>
                      <FiLock size={12} /> Lock again
                    </button>
                  )}
                </GlassCard>

                {/* A carrier waiting on a verification choice or a code shows its card right under its tile. */}
                {loginProgress[carrier.id]?.state === 'needs-you' && loginProgress[carrier.id].card && (
                  <div className="ca-detail">
                    <CarrierHandoffFrame url={loginProgress[carrier.id].card!} />
                    {loginProgress[carrier.id].runId && (
                      <button
                        type="button"
                        className="ca-detail__disconnect"
                        title="Stop this login and skip this carrier"
                        onClick={() => void cancelRun(loginProgress[carrier.id].runId!)}
                      >
                        <FiX size={13} /> Skip {carrier.name}
                      </button>
                    )}
                  </div>
                )}

                {isExpanded && (
                  <div className="ca-detail">
                      <GlassCard className="ca-detail__card" padding="var(--space-5)">
                        {carrier.browserSession && (
                          <CarrierSessionBar
                            carrierId={carrier.id}
                            carrierName={carrier.name}
                            placeholder={carrier.browserSession.placeholder}
                            startUrl={carrier.browserSession.startUrl}
                            carrierBrowserOnly={carrier.browserSession.carrierBrowserOnly}
                            optional={!credentialsStatus[carrier.id]?.viaBrowser}
                          />
                        )}
                        <CarrierAnswerRules carrierId={carrier.id} carrierName={carrier.name} />
                        <div className="ca-detail__header">
                          <div>
                            <div className="ca-detail__title">{carrier.name} actions</div>
                            <div className="ca-detail__subtitle">
                              {state?.connected
                                ? isAgencyVerified
                                  ? `Connected as ${state.username} — your agency's saved login`
                                  : isEnvVerified
                                    ? `Connected as ${state.username} — real credentials in .env.local`
                                    : `Connected as ${state.username}`
                                : 'Connect your portal login to use these automations.'}
                            </div>
                            {isAgencyVerified && (
                              agentEditId === carrier.id ? (
                                <div className="ca-detail__agent-edit">
                                  <input
                                    className="ca-detail__agent-input"
                                    value={agentDraft}
                                    placeholder="Agent name (e.g. Quote Bot)"
                                    autoFocus
                                    onChange={(e) => setAgentDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveAgent(carrier);
                                      if (e.key === 'Escape') setAgentEditId(null);
                                    }}
                                  />
                                  <button type="button" className="ca-detail__agent-save" onClick={() => handleSaveAgent(carrier)}>
                                    Save
                                  </button>
                                  <button type="button" className="ca-detail__agent-cancel" onClick={() => setAgentEditId(null)}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className={`ca-detail__agent-badge ${agencyCredentialsStatus[carrier.id]?.attachedAgent ? 'ca-detail__agent-badge--attached' : ''}`}
                                  onClick={() => {
                                    setAgentEditId(carrier.id);
                                    setAgentDraft(agencyCredentialsStatus[carrier.id]?.attachedAgent || '');
                                  }}
                                >
                                  {agencyCredentialsStatus[carrier.id]?.attachedAgent
                                    ? `Attached to ${agencyCredentialsStatus[carrier.id]?.attachedAgent}`
                                    : 'Connected to the carrier — not attached to any agent'}
                                </button>
                              )
                            )}
                          </div>
                          {state?.connected ? (
                            !isEnvVerified && (
                              <button type="button" className="ca-detail__disconnect" onClick={() => handleDisconnect(carrier)}>
                                Disconnect
                              </button>
                            )
                          ) : (
                            <button
                              type="button"
                              className="ca-detail__connect"
                              onClick={() => setCredentialCarrier(carrier)}
                            >
                              Connect
                            </button>
                          )}
                        </div>

                        <div className="ca-actions">
                          <CarrierLoginStore
                            carrierId={carrier.id}
                            carrierName={carrier.name}
                            status={credentialsStatus[carrier.id]}
                            onChanged={() => getCredentialsStatus().then(setCredentialsStatus).catch(() => {})}
                          />
                          {visibleActions(carrier).map((action) => {
                            const isDisabled = action.type === 'quote' && action.disabled;
                            const enabled = state?.enabledActions?.includes(action.key) ?? false;
                            // Every automation (View Policy List, Retrieve a
                            // Document, Create Quote, ...) sits behind the
                            // same manual toggle now — an agency turns on
                            // only what it actually wants to use.
                            const showRunner = enabled && state?.connected;
                            return (
                              <div key={action.key} className={`ca-action ${isDisabled ? 'ca-action--disabled' : ''}`}>
                                <div className="ca-action__row">
                                  <div className="ca-action__text">
                                    <div className="ca-action__label">{action.label}</div>
                                    <div className="ca-action__description">
                                      {isDisabled && action.type === 'quote' ? action.disabledReason : action.description}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className={`ca-switch ${enabled ? 'ca-switch--on' : ''}`}
                                    role="switch"
                                    aria-checked={enabled}
                                    disabled={isDisabled}
                                    onClick={() => handleActionToggle(carrier, action.key, !enabled)}
                                  >
                                    <span className="ca-switch__knob" />
                                  </button>
                                </div>
                                {action.type === 'quote' && (
                                  <div className="ca-action__row ca-action__row--sub">
                                    <div className="ca-action__text">
                                      <div className="ca-action__label">Leave browser open for me to finish</div>
                                      <div className="ca-action__description">
                                        Runs Create Quote in a visible window and leaves it open — whether it finishes clean or gets stuck — so you can take over or click Bind yourself. The automation never clicks Bind either way.
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className={`ca-switch ${headedByCarrier[carrier.id] ? 'ca-switch--on' : ''}`}
                                      role="switch"
                                      aria-checked={!!headedByCarrier[carrier.id]}
                                      onClick={() => handleToggleHeaded(carrier)}
                                    >
                                      <span className="ca-switch__knob" />
                                    </button>
                                  </div>
                                )}
                                {showRunner && (
                                  <CarrierActionRunner
                                    carrierId={carrier.id}
                                    agencyId={agencyId}
                                    action={action}
                                    policies={policiesByCarrier[carrier.id] ?? []}
                                    onPoliciesLoaded={(loaded) => setPoliciesByCarrier((prev) => ({ ...prev, [carrier.id]: loaded }))}
                                    selectedPolicyNumber={selectedPolicyByCarrier[carrier.id] ?? ''}
                                    onPolicySelected={(policyNumber) =>
                                      setSelectedPolicyByCarrier((prev) => ({ ...prev, [carrier.id]: policyNumber }))
                                    }
                                    headed={headedByCarrier[carrier.id]}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          className="ca-detail__logs-toggle"
                          onClick={() => handleToggleLogs(carrier)}
                        >
                          <span>Recent Activity</span>
                          <FiChevronDown size={14} className={`ca-tile__chevron ${logsExpandedFor === carrier.id ? 'ca-tile__chevron--open' : ''}`} />
                        </button>

                        {logsExpandedFor === carrier.id && (
                          <div className="ca-detail__logs">
                            {(runLogsByCarrier[carrier.id]?.length ?? 0) === 0 ? (
                              <div className="ca-detail__logs-empty">No runs yet for this carrier.</div>
                            ) : (
                              runLogsByCarrier[carrier.id]!.map((entry) => (
                                <div key={entry.runId} className="ca-detail__log-row">
                                  <div className="ca-detail__log-row-main">
                                    <span className={`ca-detail__log-status ca-detail__log-status--${entry.status}`}>
                                      {entry.status}
                                    </span>
                                    <span className="ca-detail__log-label">{entry.label}</span>
                                    <span className="ca-detail__log-time">{new Date(entry.finishedAt).toLocaleString()}</span>
                                  </div>
                                  {entry.status === 'failed' && (
                                    <details className="ca-detail__log-details">
                                      <summary>Log</summary>
                                      <pre>{entry.logExcerpt}</pre>
                                    </details>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </GlassCard>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </motion.div>

      <AnimatePresence>
        {credentialCarrier && (
          <CarrierCredentialModal
            carrier={credentialCarrier}
            onClose={() => {
              setCredentialCarrier(null);
              setPendingActionKey(null);
            }}
            onSubmit={handleCredentialSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CarrierAccessPage;
